'use client';
import React from 'react';
import { News, StoryObject } from '@benzinga/basic-news-manager';
import styled from '@benzinga/themetron';
import { TrackingManager } from '@benzinga/tracking-manager';
import { NewsList } from '../NewsList';
import { ContentFeedProps } from '../ContentFeed';
import { PostCardProps } from '../PostCard';
import { SessionContext } from '@benzinga/session-context';

export interface NewsListWithLoadMoreProps extends ContentFeedProps {
  nodes: News[];
  loadMoreAmount?: number;
  initialShownItems?: number;
  variant?: 'vertical' | 'horizontal';
  postCardProps?: PostCardProps;
  showCommentsIcon?: boolean;
}

export const NewsListWithLoadMore: React.FC<NewsListWithLoadMoreProps> = ({
  contentId,
  initialShownItems = 4,
  loadMoreAmount = 4,
  loadMoreDevice,
  newsItemElement,
  nodes = [],
  postCardProps = {},
  reachContent,
  showCommentsIcon,
  showSponsoredContent,
  title,
  variant,
}) => {
  const session = React.useContext(SessionContext);
  const [didLoadMore, setDidLoadMore] = React.useState(false);
  const [numberOfShownItems, setNumberOfShownItems] = React.useState(initialShownItems);
  const items = nodes.slice(0, numberOfShownItems);
  const maxNumberOfNodes = nodes.length;

  const onLoadMore = React.useCallback(() => {
    session.getManager(TrackingManager).trackTableEvent('load_more', 'news_list', 'news', {});

    if (numberOfShownItems === maxNumberOfNodes) return;
    else if (numberOfShownItems + loadMoreAmount < maxNumberOfNodes + 1) {
      setNumberOfShownItems(numberOfShownItems + loadMoreAmount);
    } else {
      const endOfList = numberOfShownItems + loadMoreAmount >= maxNumberOfNodes;
      setNumberOfShownItems(endOfList ? maxNumberOfNodes : numberOfShownItems + loadMoreAmount);
      setDidLoadMore(true);
    }
  }, [loadMoreAmount, maxNumberOfNodes, numberOfShownItems, session]);

  return (
    <div className="parsely-news-list">
      <NewsList
        contentId={contentId}
        loadMoreDevice={loadMoreDevice}
        newsItemElement={newsItemElement}
        nodes={items as StoryObject[]}
        postCardProps={postCardProps}
        reachContent={reachContent}
        showCommentsIcon={showCommentsIcon}
        showSponsoredContent={showSponsoredContent}
        title={title}
        variant={variant}
      />
      {!didLoadMore && maxNumberOfNodes > numberOfShownItems && (
        <LoadMore className="load-more-button" onClick={onLoadMore} tabIndex={0}>
          Load more
        </LoadMore>
      )}
    </div>
  );
};

const LoadMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 5px 0;
  font-weight: bold;
  color: ${({ theme }) => theme.colorPalette.blue500};
  width: 100%;
  padding: 10px 0;
  cursor: pointer;
`;
